import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ProductDTO } from '@horology/shared-types';
import { importProductsCsv, listProducts, type ImportResult } from './products.api';
import { ProductForm } from './ProductForm';
import { formatCop, imageUrl } from '../../lib/format';
import { hasPermission, useMe } from '../../hooks/useAuth';
import { readApiError } from '../../lib/api';

export function InventoryPage() {
  const me = useMe();
  const canWrite = hasPermission(me.data, 'inventory.write');

  const [q, setQ] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState<ProductDTO | null | undefined>(undefined); // undefined = closed, null = new
  const [showImport, setShowImport] = useState(false);

  const qc = useQueryClient();
  const products = useQuery({
    queryKey: ['products', { q, includeInactive }],
    queryFn: () => listProducts({ q: q || undefined, includeInactive, limit: 100 }),
  });

  const onSaved = () => {
    setEditing(undefined);
    void qc.invalidateQueries({ queryKey: ['products'] });
  };

  if (editing !== undefined) {
    return <ProductForm initial={editing} onSaved={onSaved} onCancel={() => setEditing(undefined)} />;
  }

  return (
    <section>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
        <h1 style={{ margin: 0 }}>Inventario</h1>
        {canWrite && (
          <>
            <button className="btn btn-primary" onClick={() => setEditing(null)}>
              + Nuevo producto
            </button>
            <button className="btn" onClick={() => setShowImport(true)}>
              Importar CSV
            </button>
            <a className="btn" href="/api/products/export.csv">
              Exportar CSV
            </a>
          </>
        )}
      </div>

      {showImport && (
        <CsvImportModal
          onCancel={() => setShowImport(false)}
          onApplied={() => {
            setShowImport(false);
            void qc.invalidateQueries({ queryKey: ['products'] });
          }}
        />
      )}

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        <input
          className="input"
          placeholder="Buscar por SKU o nombre..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ maxWidth: 320 }}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.875rem' }}>
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          />
          Mostrar inactivos
        </label>
      </div>

      {products.isLoading && <p>Cargando...</p>}
      {products.isError && <p className="error">No se pudo cargar el inventario.</p>}

      {products.data && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--color-bg)', textAlign: 'left' }}>
                <th style={{ padding: '0.75rem 1rem', width: 64 }}></th>
                <th style={{ padding: '0.75rem 1rem' }}>SKU</th>
                <th style={{ padding: '0.75rem 1rem' }}>Nombre</th>
                <th style={{ padding: '0.75rem 1rem' }}>Precio</th>
                <th style={{ padding: '0.75rem 1rem' }}>Stock</th>
                <th style={{ padding: '0.75rem 1rem' }}>Estado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {products.data.data.map((p) => {
                const totalStock =
                  p.stocks?.reduce((s, x) => s + Number(x.quantity), 0) ?? 0;
                return (
                  <tr key={p.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '0.5rem 1rem' }}>
                      {p.imagePath ? (
                        <img
                          src={imageUrl(p.imagePath)}
                          alt=""
                          style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4 }}
                        />
                      ) : (
                        <div style={{ width: 48, height: 48, background: 'var(--color-bg)', borderRadius: 4 }} />
                      )}
                    </td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <code>{p.sku}</code>
                      {p.isFavorite && <span title="Favorito"> ★</span>}
                    </td>
                    <td style={{ padding: '0.75rem 1rem' }}>{p.name}</td>
                    <td style={{ padding: '0.75rem 1rem' }}>{formatCop(p.salePrice)}</td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      {p.isService ? <em style={{ color: 'var(--color-muted)' }}>servicio</em> : totalStock}
                    </td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      {p.isActive ? (
                        <span style={{ color: 'var(--color-accent)' }}>activo</span>
                      ) : (
                        <span style={{ color: 'var(--color-muted)' }}>inactivo</span>
                      )}
                    </td>
                    <td style={{ padding: '0.5rem 1rem', textAlign: 'right' }}>
                      {canWrite && (
                        <button className="btn" onClick={() => setEditing(p)}>
                          Editar
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {products.data.data.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-muted)' }}>
                    Sin productos. {canWrite && 'Crea el primero con el botón superior.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function CsvImportModal(props: { onCancel: () => void; onApplied: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const previewMut = useMutation({
    mutationFn: () => {
      if (!file) throw new Error('No file');
      return importProductsCsv(file, true);
    },
    onSuccess: (r) => setPreview(r),
    onError: (e) => setError(readApiError(e).message),
  });

  const applyMut = useMutation({
    mutationFn: () => {
      if (!file) throw new Error('No file');
      return importProductsCsv(file, false);
    },
    onSuccess: () => props.onApplied(),
    onError: (e) => setError(readApiError(e).message),
  });

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
      }}
    >
      <div className="card" style={{ width: 'min(720px, 92vw)', maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ marginTop: 0 }}>Importar productos desde CSV</h3>
        <p style={{ color: 'var(--color-muted)', fontSize: '0.875rem', marginTop: 0 }}>
          Columnas reconocidas (en cualquier orden): <code>sku</code>, <code>name</code>,{' '}
          <code>description</code>, <code>purchasePrice</code>, <code>taxRate</code>,{' '}
          <code>salePrice</code>, <code>isService</code>, <code>isFavorite</code>,{' '}
          <code>isActive</code>. <strong>sku es la clave</strong> — productos existentes se
          actualizan; nuevos se insertan.
        </p>

        <div className="field">
          <label>Archivo .csv</label>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
              setPreview(null);
            }}
          />
        </div>

        {error && <p className="error">{error}</p>}

        {!preview && (
          <button
            className="btn"
            disabled={!file || previewMut.isPending}
            onClick={() => previewMut.mutate()}
          >
            {previewMut.isPending ? 'Analizando...' : 'Vista previa'}
          </button>
        )}

        {preview && (
          <div style={{ marginTop: '1rem' }}>
            <div
              style={{
                display: 'flex',
                gap: '0.75rem',
                background: 'var(--color-bg)',
                padding: '0.6rem 0.75rem',
                borderRadius: 'var(--radius)',
              }}
            >
              <span style={{ color: 'var(--color-accent)' }}>
                + {preview.summary.inserted} nuevo(s)
              </span>
              <span>↻ {preview.summary.updated} actualizado(s)</span>
              {preview.errors && preview.errors.length > 0 && (
                <span style={{ color: 'var(--color-danger)' }}>
                  ✗ {preview.errors.length} error(es)
                </span>
              )}
            </div>

            {preview.errors && preview.errors.length > 0 && (
              <ul style={{ color: 'var(--color-danger)', fontSize: '0.85rem' }}>
                {preview.errors.slice(0, 10).map((e, i) => (
                  <li key={i}>
                    Línea {e.line}: {e.message}
                  </li>
                ))}
                {preview.errors.length > 10 && <li>… y {preview.errors.length - 10} más</li>}
              </ul>
            )}

            {preview.rows && preview.rows.length > 0 && (
              <div style={{ maxHeight: 280, overflowY: 'auto', marginTop: '0.5rem' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ background: 'var(--color-bg)' }}>
                      <th style={{ padding: '0.4rem 0.6rem', textAlign: 'left' }}>SKU</th>
                      <th style={{ padding: '0.4rem 0.6rem', textAlign: 'left' }}>Nombre</th>
                      <th style={{ padding: '0.4rem 0.6rem', textAlign: 'right' }}>Precio Venta</th>
                      <th style={{ padding: '0.4rem 0.6rem', textAlign: 'right' }}>IVA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.slice(0, 30).map((r, i) => (
                      <tr key={i} style={{ borderTop: '1px solid var(--color-border)' }}>
                        <td style={{ padding: '0.3rem 0.6rem' }}>
                          <code>{r.sku}</code>
                        </td>
                        <td style={{ padding: '0.3rem 0.6rem' }}>{(r.name as string) ?? '—'}</td>
                        <td style={{ padding: '0.3rem 0.6rem', textAlign: 'right' }}>
                          {r.salePrice !== undefined ? formatCop(r.salePrice as number) : '—'}
                        </td>
                        <td style={{ padding: '0.3rem 0.6rem', textAlign: 'right' }}>
                          {r.taxRate !== undefined ? `${r.taxRate}%` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {preview.rows.length > 30 && (
                  <p style={{ fontSize: '0.8rem', color: 'var(--color-muted)' }}>
                    Mostrando 30 de {preview.rows.length} filas.
                  </p>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
              <button
                className="btn btn-primary"
                disabled={applyMut.isPending || !preview.ok}
                onClick={() => applyMut.mutate()}
              >
                {applyMut.isPending ? 'Importando...' : 'Confirmar importación'}
              </button>
              <button className="btn" onClick={() => setPreview(null)}>
                Volver a analizar
              </button>
              <button className="btn" onClick={props.onCancel}>
                Cancelar
              </button>
            </div>
          </div>
        )}

        {!preview && (
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
            <button className="btn" onClick={props.onCancel}>
              Cancelar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
